"use client";

import React, { useState } from "react";
import Sidebar from "../common/Sidebar";
import CreateGroupModal from "./CreateGroupModal";
import GroupModal from "./GroupModal";
import Header from "../common/Header";
import PageWrapper from "../../../components/admin/common/PageWrapper";
import { Trash } from "lucide-react";
import { deleteGroup } from "../../../services/groupServices";
import DeleteConfirmationModal from "../members/DeleteMemberModal";

const SmsGroupManager = ({ users, refreshGroups }) => {
  const [open, setOpen] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selected, setSelected] = useState(null);

  // Add state for delete modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Handle delete button click
  const handleDeleteClick = (group) => {
    setGroupToDelete(group);
    setDeleteModalOpen(true);
  };

  // Handle actual deletion
  const handleDeleteConfirm = async () => {
    if (!groupToDelete) return;

    setIsDeleting(true);
    try {
      await deleteGroup(groupToDelete.groupDetails._id);
      await refreshGroups();
      setDeleteModalOpen(false);
      setGroupToDelete(null);
    } catch (error) {
      console.error("Failed to delete group:", error);
      // You might want to show an error toast here
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle cancel deletion
  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setGroupToDelete(null);
  };

  return (
    <>
      <div className="flex h-screen p-4 gap-4 bg-white overflow-hidden">
        <div className="h-full">
          <div className="sticky top-0 h-screen text-white">
            <Sidebar />
          </div>
        </div>
        <div className="flex-1 h-full bg-[#F3EFFF] rounded-[27px] overflow-hidden">
          <PageWrapper>
            <div className="flex flex-col h-full">
              <div className="flex-1 p-4 overflow-y-auto">
                <Header
                  title="Manage SMS Groups"
                  desc="Create or Manage Groups here"
                />
                <section className="flex justify-end w-full mb-6 mt-4">
                  <button
                    onClick={() => setOpen(true)}
                    className="bg-[#4F22D8] cursor-pointer border border-[#0000003D] text-white px-4 py-2 text-sm font-semibold rounded-[10px] hover:opacity-90 transition"
                  >
                    + Create Group
                  </button>
                </section>

                <section className="bg-white rounded-xl overflow-hidden border border-[#D5D5D5]">
                  <table className="min-w-full text-sm font-medium text-white">
                    <thead className="bg-[#4F22D8]">
                      <tr>
                        <th className="px-4 py-3 text-left">No</th>
                        <th className="px-4 py-3 text-center">Group Name</th>
                        <th className="px-4 py-3 text-center">Total Users</th>
                        <th className="px-4 py-3 text-center">Created On</th>
                        <th className="px-4 py-3 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.length === 0 ? (
                        <tr>
                          <td
                            colSpan={5}
                            className="text-center text-[#696969] py-6"
                          >
                            No groups found.
                          </td>
                        </tr>
                      ) : (
                        users.map((group, idx) => (
                          <tr
                            key={idx}
                            className="text-sm text-[#696969] font-normal border-b border-[#B0B0B075] last:border-0"
                          >
                            <td className="px-4 py-3">{group.no}</td>
                            <td className="px-4 py-3 text-center">
                              {group.groupDetails?.groupName || "Unnamed"}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {group.totalUsers ?? "0"}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {group.createdOn || "N/A"}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <button
                                type="button"
                                onClick={() => {
                                  setSelected(group);
                                  setOpenModal(true);
                                }}
                                className="bg-[#E0E3FF] cursor-pointer text-[#1E30D4] px-4 py-1 rounded-[10px] font-semibold text-xs shadow-sm hover:opacity-90 transition"
                                aria-label={`View details for group ${
                                  group.groupDetails?.groupName || "Unknown"
                                }`}
                              >
                                View
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteClick(group)}
                                disabled={isDeleting}
                                className="bg-[#FFE0E0] ml-2 cursor-pointer text-[#D41E1E] px-3 py-1 rounded-[10px] font-semibold text-xs shadow-sm hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                aria-label={`Delete group ${
                                  group.groupDetails?.groupName || "Unknown"
                                }`}
                              >
                                <Trash size={16} />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </section>
              </div>
            </div>
          </PageWrapper>
        </div>
      </div>

      {/* Existing Modals */}
      {open && (
        <CreateGroupModal
          isOpen={open}
          close={() => setOpen(false)}
          refreshGroups={refreshGroups}
        />
      )}
      {openModal && (
        <GroupModal
          isOpen={openModal}
          group={selected}
          onClose={() => setOpenModal(false)}
          refreshGroups={refreshGroups}
        />
      )}

      {deleteModalOpen && (
        <DeleteConfirmationModal
          isOpen={deleteModalOpen}
          onRequestClose={handleDeleteCancel}
          onDelete={handleDeleteConfirm}
          itemName={groupToDelete?.groupDetails?.groupName || "Unknown Group"}
          itemType="group"
        />
      )}
    </>
  );
};

export default SmsGroupManager;
